// Sample page that queries SOR records using the app's Auth0 session.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from '@nextui-org/react';
import { CtyButton, useSorApi } from '@circuitry-ai/doc-ui';

const PAGE_SIZE = 25;
const DEFINITION = 'core.workqueue.queue';

type SorRecord = Record<string, unknown>;

// The response envelope is not fixed yet, so accept the common shapes
const extractRecords = (
  body: any
): { records: SorRecord[]; total?: number } => {
  if (Array.isArray(body)) return { records: body };
  const records =
    body?.records ?? body?.items ?? body?.data ?? body?.results ?? [];
  const total = body?.total ?? body?.count ?? body?.page?.total;
  return {
    records: Array.isArray(records) ? records : [],
    total: typeof total === 'number' ? total : undefined,
  };
};

const formatCell = (value: unknown) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export function SorRecordsSample() {
  const { queryRecords } = useSorApi();

  const [records, setRecords] = useState<SorRecord[]>([]);
  const [total, setTotal] = useState<number | undefined>();
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(
    async (pageOffset: number, signal?: AbortSignal) => {
      setIsLoading(true);
      setError(null);

      try {
        const body = await queryRecords(
          {
            definition: DEFINITION,
            filter: null,
            search: null,
            state: null,
            include_deleted: false,
            page: { limit: PAGE_SIZE, offset: pageOffset },
          },
          signal
        );
        const result = extractRecords(body);
        setRecords(result.records);
        setTotal(result.total);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        console.error('Error querying SOR records:', err);
        setError((err as Error).message);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [queryRecords]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchRecords(offset, controller.signal);
    return () => controller.abort();
  }, [offset, fetchRecords]);

  const columns = useMemo(() => {
    const keys = new Set<string>();
    records.forEach((record) =>
      Object.keys(record).forEach((key) => keys.add(key))
    );
    return Array.from(keys);
  }, [records]);

  const hasNext =
    total === undefined
      ? records.length === PAGE_SIZE
      : offset + PAGE_SIZE < total;

  // Loading, then error (shown above), then empty, then the table
  const renderRecords = () => {
    if (isLoading) {
      return (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="sm" />
        </div>
      );
    }
    if (error) {
      return null;
    }
    if (records.length === 0) {
      return (
        <div className="flex h-40 items-center justify-center text-sm text-default-500">
          No records found
        </div>
      );
    }
    return (
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-default-100">
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                className="px-3 py-2 text-left font-medium text-default-700 whitespace-nowrap"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr
              key={String(record.id ?? index)}
              className="border-t border-default-200"
            >
              {columns.map((column) => (
                <td
                  key={column}
                  className="px-3 py-2 text-default-600 max-w-xs truncate"
                  title={formatCell(record[column])}
                >
                  {formatCell(record[column])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="flex flex-col gap-4 p-6 h-full min-h-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">SOR Records</h1>
          <p className="text-sm text-default-500">{DEFINITION}</p>
        </div>
        <CtyButton
          size="sm"
          color="primary"
          isDisabled={isLoading}
          onPress={() => fetchRecords(offset)}
        >
          Refresh
        </CtyButton>
      </div>

      {error && (
        <div className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger break-all">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-default-200">
        {renderRecords()}
      </div>

      <div className="flex items-center justify-between text-sm text-default-500">
        <span>
          {records.length > 0
            ? `${offset + 1}–${offset + records.length}`
            : '0'}
          {total !== undefined && ` of ${total}`}
        </span>
        <div className="flex gap-2">
          <CtyButton
            size="sm"
            variant="flat"
            isDisabled={isLoading || offset === 0}
            onPress={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Previous
          </CtyButton>
          <CtyButton
            size="sm"
            variant="flat"
            isDisabled={isLoading || !hasNext}
            onPress={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </CtyButton>
        </div>
      </div>
    </div>
  );
}

export default SorRecordsSample;
